import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton component
const PolicySkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="border border-light-border dark:border-border rounded-xl p-6">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-48 mb-4"></div>
      <div className="h-10 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
    </div>
    <div className="border border-light-border dark:border-border rounded-xl p-6">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-56 mb-4"></div>
      <div className="flex gap-4">
        <div className="h-10 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
        <div className="h-10 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
      </div>
    </div>
    <div className="border border-light-border dark:border-border rounded-xl p-6">
      <div className="h-4 bg-slate-200 dark:bg-silver/20 rounded w-40 mb-4"></div>
      <div className="h-10 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
    </div>
  </div>
);

const DoctorCancellationPolicy = () => {
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  // State
  const [policy, setPolicy] = useState({
    freeCancellationHours: 24,
    partialRefundPercentage: 50,
    partialRefundHours: 2,
    noRefundHours: 2
  });
  const [originalPolicy, setOriginalPolicy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [doctorId, setDoctorId] = useState(null);

  // Check for unsaved changes
  const hasUnsavedChanges = originalPolicy && (
    policy.freeCancellationHours !== originalPolicy.freeCancellationHours ||
    policy.partialRefundPercentage !== originalPolicy.partialRefundPercentage ||
    policy.partialRefundHours !== originalPolicy.partialRefundHours ||
    policy.noRefundHours !== originalPolicy.noRefundHours
  );

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch doctor ID
  useEffect(() => {
    const fetchDoctorId = async () => {
      if (!isAuthenticated || user?.role !== 'doctor') return;

      try {
        const doctorsResponse = await fetch(`${API_URL}/doctors`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (doctorsResponse.ok) {
          const doctorsData = await doctorsResponse.json();
          const myDoctor = doctorsData.doctors?.find(
            doc => doc.firstName === user.firstName && doc.lastName === user.lastName
          );
          if (myDoctor) {
            setDoctorId(myDoctor.id);
          }
        }
      } catch (err) {
        console.error('Error fetching doctor ID:', err);
      }
    };

    fetchDoctorId();
  }, [isAuthenticated, user, token]);

  // Fetch cancellation policy
  const fetchPolicy = useCallback(async () => {
    if (!doctorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/cancellation-policy`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const loadedPolicy = {
          freeCancellationHours: data.policy?.freeCancellationHours ?? 24,
          partialRefundPercentage: data.policy?.partialRefundPercentage ?? 50,
          partialRefundHours: data.policy?.partialRefundHours ?? 2,
          noRefundHours: data.policy?.noRefundHours ?? 2
        };
        setPolicy(loadedPolicy);
        setOriginalPolicy(loadedPolicy);
      } else if (response.status === 404) {
        // No policy set yet, use defaults
        const defaultPolicy = {
          freeCancellationHours: 24,
          partialRefundPercentage: 50,
          partialRefundHours: 2,
          noRefundHours: 2
        };
        setPolicy(defaultPolicy);
        setOriginalPolicy(defaultPolicy);
      } else {
        throw new Error('Failed to fetch cancellation policy');
      }
    } catch (err) {
      console.error('Error fetching policy:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [doctorId, token]);

  useEffect(() => {
    if (doctorId) {
      fetchPolicy();
    }
  }, [doctorId, fetchPolicy]);

  // Save policy
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Validate policy settings
      if (policy.freeCancellationHours < 0) {
        throw new Error('Free cancellation hours cannot be negative');
      }
      if (policy.partialRefundPercentage < 0 || policy.partialRefundPercentage > 100) {
        throw new Error('Partial refund percentage must be between 0 and 100');
      }
      if (policy.partialRefundHours < 0) {
        throw new Error('Partial refund hours cannot be negative');
      }
      if (policy.noRefundHours < 0) {
        throw new Error('No refund hours cannot be negative');
      }
      // Ensure hours make sense (free > partial > no refund)
      if (policy.partialRefundHours >= policy.freeCancellationHours) {
        throw new Error('Free cancellation period must be longer than partial refund period');
      }
      if (policy.noRefundHours > policy.partialRefundHours) {
        throw new Error('No refund period cannot be longer than partial refund period');
      }

      const response = await fetch(`${API_URL}/doctors/${doctorId}/cancellation-policy`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          freeCancellationHours: policy.freeCancellationHours,
          partialRefundPercentage: policy.partialRefundPercentage,
          partialRefundHours: policy.partialRefundHours,
          noRefundHours: policy.noRefundHours
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save cancellation policy');
      }

      setOriginalPolicy({ ...policy });
      toast.success('Cancellation policy saved successfully!');
    } catch (err) {
      console.error('Error saving policy:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to defaults
  const handleResetDefaults = () => {
    setPolicy({
      freeCancellationHours: 24,
      partialRefundPercentage: 50,
      partialRefundHours: 2,
      noRefundHours: 2
    });
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <Link
              to="/doctor/dashboard"
              className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver transition-smooth mb-2 inline-block"
            >
              &larr; Back to Dashboard
            </Link>
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight text-light-text dark:text-pure-white">
              Cancellation Policy
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60 mt-2">
              Configure how cancellations and refunds are handled for your appointments
            </p>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400 font-mono text-sm">{error}</p>
          </div>
        )}

        {/* Main content */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          {isLoading ? (
            <PolicySkeleton />
          ) : (
            <div className="space-y-8">
              {/* Free Cancellation Period */}
              <div className="border-b border-light-border dark:border-border pb-6">
                <label className="block font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Free Cancellation Period
                </label>
                <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mb-4">
                  Patients can cancel and receive a full refund if they cancel at least this many hours before the appointment.
                </p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0"
                    max="168"
                    value={policy.freeCancellationHours}
                    onChange={(e) => setPolicy(prev => ({
                      ...prev,
                      freeCancellationHours: parseInt(e.target.value) || 0
                    }))}
                    className="w-24 px-3 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-center text-light-text dark:text-pure-white focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth"
                    data-testid="free-cancellation-hours"
                  />
                  <span className="font-body text-light-text-secondary dark:text-silver/60">hours before appointment</span>
                </div>
                <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-2">
                  Example: {policy.freeCancellationHours} hours = {policy.freeCancellationHours >= 24 ? `${Math.floor(policy.freeCancellationHours / 24)} day${Math.floor(policy.freeCancellationHours / 24) !== 1 ? 's' : ''}` : 'same day'}
                  {policy.freeCancellationHours % 24 > 0 && policy.freeCancellationHours >= 24 ? ` ${policy.freeCancellationHours % 24} hours` : ''}
                </p>
              </div>

              {/* Partial Refund Period */}
              <div className="border-b border-light-border dark:border-border pb-6">
                <label className="block font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Partial Refund Period
                </label>
                <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mb-4">
                  Patients who cancel between {policy.partialRefundHours} and {policy.freeCancellationHours} hours before the appointment receive a partial refund.
                </p>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="font-body text-light-text-secondary dark:text-silver/60">Refund</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={policy.partialRefundPercentage}
                    onChange={(e) => setPolicy(prev => ({
                      ...prev,
                      partialRefundPercentage: parseInt(e.target.value) || 0
                    }))}
                    className="w-20 px-3 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-center text-light-text dark:text-pure-white focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth"
                    data-testid="partial-refund-percentage"
                  />
                  <span className="font-body text-light-text-secondary dark:text-silver/60">% if cancelled between</span>
                  <input
                    type="number"
                    min="0"
                    max={policy.freeCancellationHours}
                    value={policy.partialRefundHours}
                    onChange={(e) => setPolicy(prev => ({
                      ...prev,
                      partialRefundHours: parseInt(e.target.value) || 0
                    }))}
                    className="w-20 px-3 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-center text-light-text dark:text-pure-white focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth"
                    data-testid="partial-refund-hours"
                  />
                  <span className="font-body text-light-text-secondary dark:text-silver/60">and {policy.freeCancellationHours} hours</span>
                </div>
                <p className="font-mono text-xs text-slate-400 dark:text-silver/40">
                  Example: For a {'\u00A3'}100 appointment, patient receives {'\u00A3'}{policy.partialRefundPercentage} refund
                </p>
              </div>

              {/* No Refund Period */}
              <div className="pb-6">
                <label className="block font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  No Refund Period
                </label>
                <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mb-4">
                  Patients who cancel less than {policy.noRefundHours} hours before the appointment receive no refund.
                </p>
                <div className="flex items-center gap-3">
                  <span className="font-body text-light-text-secondary dark:text-silver/60">No refund if cancelled within</span>
                  <input
                    type="number"
                    min="0"
                    max={policy.partialRefundHours}
                    value={policy.noRefundHours}
                    onChange={(e) => setPolicy(prev => ({
                      ...prev,
                      noRefundHours: parseInt(e.target.value) || 0
                    }))}
                    className="w-20 px-3 py-2 bg-slate-100 dark:bg-charcoal border border-light-border dark:border-border rounded-lg font-mono text-center text-light-text dark:text-pure-white focus:border-slate-400 dark:focus:border-silver/40 focus:outline-none transition-smooth"
                    data-testid="no-refund-hours"
                  />
                  <span className="font-body text-light-text-secondary dark:text-silver/60">hours of appointment</span>
                </div>
              </div>

              {/* Policy Summary */}
              <div className="bg-slate-100 dark:bg-charcoal/50 border border-light-border dark:border-border rounded-xl p-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Policy Summary
                </h3>
                <ul className="space-y-2 font-body text-sm text-slate-600 dark:text-silver/80">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400">&#x2713;</span>
                    <span>Full refund: Cancel {policy.freeCancellationHours}+ hours before appointment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-400">&#x2713;</span>
                    <span>{policy.partialRefundPercentage}% refund: Cancel {policy.partialRefundHours}-{policy.freeCancellationHours} hours before appointment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">&#x2717;</span>
                    <span>No refund: Cancel less than {policy.noRefundHours} hours before appointment</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4">
                <button
                  onClick={handleResetDefaults}
                  disabled={isSaving}
                  className="px-4 py-2 border border-light-border dark:border-border rounded-xl font-mono text-xs uppercase tracking-widest text-light-text dark:text-silver hover:border-slate-400 dark:hover:border-silver/40 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reset to Defaults
                </button>
                <div className="flex items-center gap-4">
                  {hasUnsavedChanges && (
                    <span className="font-mono text-xs text-yellow-400">
                      Unsaved changes
                    </span>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !hasUnsavedChanges}
                    className="px-6 py-2 bg-gradient-to-r from-silver to-pure-white text-obsidian rounded-xl font-mono text-sm uppercase tracking-widest hover:opacity-90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-policy-button"
                  >
                    {isSaving ? 'Saving...' : 'Save Policy'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Help text */}
        <div className="mt-6 p-4 border border-light-border dark:border-border rounded-xl">
          <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
            About Cancellation Policies
          </h3>
          <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
            Your cancellation policy will be displayed to patients when they book an appointment.
            Clear policies help set expectations and protect both you and your patients.
            Common practice is to allow free cancellation up to 24 hours before the appointment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DoctorCancellationPolicy;
